package com.kanati.shared.commerce

import kotlinx.serialization.Serializable

@Serializable
data class CreateSaleRequest(
    val districtId: String,
    val schoolId: String = "",
    val paymentMethod: String,
    val paymentIntentId: String? = null,
    val loyaltyAccountId: String? = null,
    val rewardId: String? = null,
    val lineItems: List<CreateSaleLineItemRequest>,
)

@Serializable
data class CreateSaleLineItemRequest(
    val productId: String,
    val quantity: Int,
)

@Serializable
enum class PaymentStatus {
    COMPLETED,
}

@Serializable
enum class PaymentProcessor {
    NONE,
    STRIPE,
}

@Serializable
data class StartTerminalSalePaymentRequest(
    val sale: CreateSaleRequest,
    val terminalReaderId: String,
)

@Serializable
data class TerminalConnectionTokenResponse(
    val secret: String,
)

/**
 * Backend configuration readiness, not device eligibility or payment approval.
 * Disabled configuration may omit unavailableReason; clients supply a safe fallback message.
 * Reasons are display text and must not contain payment credentials.
 */
@Serializable
data class PosTapToPayConfigResponse(
    val enabled: Boolean,
    val locationId: String? = null,
    val merchantDisplayName: String,
    val unavailableReason: String? = null,
)

@Serializable
data class StartClientTerminalSalePaymentRequest(
    val sale: CreateSaleRequest,
)

@Serializable
data class ClientTerminalPaymentSessionResponse(
    val paymentSessionId: String,
    val paymentIntentId: String,
    val clientSecret: String,
    val amount: Long,
    val currencyCode: String,
    val flowType: TerminalPaymentFlowType,
    val finalizationStatus: TerminalPaymentFinalizationStatus,
    val message: String,
)

@Serializable
data class FinalizeClientTerminalSalePaymentRequest(
    val paymentSessionId: String,
)

@Serializable
data class RecoverClientTerminalSalePaymentResponse(
    val session: ClientTerminalPaymentSessionResponse,
    val sale: SaleResponse? = null,
)

@Serializable
data class TerminalReaderDto(
    val id: String,
    val label: String? = null,
    val deviceType: String,
    val serialNumber: String? = null,
    val locationId: String? = null,
    val locationDisplayName: String? = null,
    val status: String,
)

@Serializable
enum class TerminalPaymentStatus {
    PENDING,
    SUCCEEDED,
    FAILED,
    CANCELED,
}

@Serializable
enum class TerminalPaymentFlowType {
    CONCESSIONS_SALE,
    GATE_TICKET_SALE,
}

@Serializable
enum class TerminalPaymentFinalizationStatus {
    AWAITING_PAYMENT,
    READY_TO_FINALIZE,
    FINALIZED,
    FAILED,
    CANCELED,
}

@Serializable
data class TerminalPaymentSessionResponse(
    val paymentIntentId: String,
    val terminalReaderId: String,
    val terminalReaderLabel: String? = null,
    val amount: Long,
    val currencyCode: String,
    val status: TerminalPaymentStatus,
    val message: String,
    val failureCode: String? = null,
    val flowType: TerminalPaymentFlowType? = null,
    val finalizationStatus: TerminalPaymentFinalizationStatus = TerminalPaymentFinalizationStatus.AWAITING_PAYMENT,
)

@Serializable
data class RecoverTerminalSalePaymentResponse(
    val session: TerminalPaymentSessionResponse,
    val sale: SaleResponse? = null,
)

@Serializable
data class SaleResponse(
    val id: String,
    val districtId: String,
    val schoolId: String = "",
    val createdAt: String,
    val paymentMethod: String,
    val paymentStatus: PaymentStatus = PaymentStatus.COMPLETED,
    val paymentProcessor: PaymentProcessor = PaymentProcessor.NONE,
    val paymentReference: String? = null,
    val subtotalAmount: Long,
    val taxAmount: Long,
    val discountAmount: Long = 0,
    val totalAmount: Long,
    val loyaltyAccountDisplayName: String? = null,
    val loyaltyPointsEarned: Int? = null,
    val loyaltyBalanceAfter: Int? = null,
    val appliedRewardName: String? = null,
    val appliedRewardDiscountAmount: Long? = null,
    val lineItems: List<SaleLineItemResponse>,
)

@Serializable
data class SaleLineItemResponse(
    val id: String,
    val saleId: String,
    val productId: String,
    val productNameSnapshot: String,
    val unitPriceAmount: Long,
    val quantity: Int,
    val lineSubtotalAmount: Long,
    val taxable: Boolean,
)
